<template>
  <page-main title="Targeted Resume Builder" noBottom >
    <!-- PR-UPGRADE-MODAL: the per-tool premium-banner was replaced by the one
         shared UpgradeToPlusModal (mounted globally in App.vue). Gating below
         is unchanged; it opens the shared modal via `upgradeModal/open`. -->
    <template v-slot:aside>
      <div>
        <p>
          Paste a job description and let AI curate your existing profile evidence
          into a tailored resume. Every bullet traces back to your real achievements.
        </p>
        <ul>
          <li>
            <span class="font-weight-bold">Evidence-Based:</span> Only includes
            achievements, skills, and credentials that exist in your Resumation profile.
          </li>
          <li>
            <span class="font-weight-bold">Sync Notes:</span> Honest gap reporting
            shows where your profile doesn't match the JD requirements.
          </li>
          <li>
            <span class="font-weight-bold">Download:</span> Export as a formatted
            DOCX matching the Professional Resume template.
          </li>
        </ul>
      </div>
    </template>
    <template v-slot:nav>
      <v-btn outlined class="mt-4" block @click="$router.push('/JobFitAnalyzer')">
        Try Job Fit Analyzer
      </v-btn>
    </template>
    <v-container fluid class="container-main">
      <v-alert type="info" outlined dense class="mb-4">
        AI-assisted content — always review for accuracy before sharing.
      </v-alert>

      <div v-if="step === 'input'">
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="jobDescription"
              label="Paste the full job description"
              placeholder="Copy and paste the entire job description here (job title, description, qualifications, etc.)"
              outlined
              rows="10"
              :counter="8000"
              :rules="[jdRule]"
              hint="Maximum 8,000 characters"
              persistent-hint
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="additionalContext"
              label="What should this resume emphasize, or how should it read? (optional)"
              placeholder="e.g. Emphasize team leadership and budget ownership; keep the tone confident but not boastful."
              outlined
              rows="3"
              :counter="2000"
              :rules="[contextRule]"
              hint="Steers emphasis and tone only. It will not add anything that is not already in your profile."
              persistent-hint
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="12" sm="4">
            <v-select
              v-model="pageLength"
              :items="pageLengthOptions"
              label="Resume Length"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-file-document-outline"
              @change="onPageLengthChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-switch
              v-model="keepAllJobs"
              label="Keep all jobs (no roles dropped)"
              hide-details
              dense
              class="mt-1"
            ></v-switch>
          </v-col>
          <v-col cols="12" sm="4" class="text-right">
            <v-btn
              color="primary"
              large
              :disabled="!jobDescription.trim() || jobDescription.length > 8000 || generating"
              :loading="generating"
              @click="generateResume"
            >
              <v-icon left>mdi-auto-fix</v-icon>
              Generate Targeted Resume
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <div v-if="step === 'loading'" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="64" class="mb-4"></v-progress-circular>
        <div class="text-h6 mb-2">Analyzing your profile against this role...</div>
        <div class="text-body-2 text--secondary">Selecting the most relevant evidence from your career profile</div>
      </div>

      <div v-if="step === 'result'">
        <v-row>
          <v-col cols="12" md="8">
            <v-card outlined class="pa-6 resume-preview-card">
              <div class="mb-6">
                <div class="text-h5 font-weight-bold text-left">{{ displayName }}</div>
                <div v-if="displayContactLine" class="text-body-2 text--secondary text-left">{{ displayContactLine }}</div>
              </div>

              <div v-if="result.professionalSummary" class="mb-5">
                <div class="section-label">PROFESSIONAL SUMMARY</div>
                <v-divider class="mb-2"></v-divider>
                <div class="text-body-2">{{ result.professionalSummary }}</div>
              </div>

              <div v-if="result.skillsAndAbilities && result.skillsAndAbilities.length > 0" class="mb-5">
                <div class="section-label">SKILLS AND ABILITIES</div>
                <v-divider class="mb-2"></v-divider>
                <div v-for="(group, gi) in result.skillsAndAbilities" :key="'sg-' + gi" class="mb-2">
                  <span class="font-weight-medium text-body-2">{{ group.category }}: </span>
                  <span class="text-body-2">{{ (group.items || []).join(', ') }}</span>
                </div>
              </div>

              <div v-if="result.experience && result.experience.length > 0" class="mb-5">
                <div class="section-label">EXPERIENCE</div>
                <v-divider class="mb-2"></v-divider>
                <div v-for="(job, ji) in result.experience" :key="'job-' + ji" class="mb-4">
                  <div class="d-flex justify-space-between align-start">
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">{{ job.title }}</div>
                      <div class="text-body-2 text--secondary">{{ job.company }}{{ job.location ? ', ' + job.location : '' }}</div>
                    </div>
                    <div class="text-caption text--secondary text-right" style="white-space: nowrap;">
                      {{ job.startDate }} – {{ job.endDate }}
                    </div>
                  </div>
                  <ul v-if="job.bullets && job.bullets.length > 0" class="mt-2 resume-bullets">
                    <li v-for="(bullet, bi) in job.bullets" :key="'b-' + ji + '-' + bi" class="text-body-2 mb-1">
                      {{ bullet }}
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="result.education && result.education.length > 0" class="mb-5">
                <div class="section-label">EDUCATION</div>
                <v-divider class="mb-2"></v-divider>
                <div v-for="(edu, ei) in result.education" :key="'edu-' + ei" class="mb-2">
                  <div class="d-flex justify-space-between">
                    <div>
                      <div class="text-body-2 font-weight-medium">{{ edu.degree }}</div>
                      <div class="text-body-2 text--secondary">{{ edu.school }}{{ edu.location ? ', ' + edu.location : '' }}</div>
                    </div>
                    <div class="text-caption text--secondary">{{ edu.dates }}</div>
                  </div>
                </div>
              </div>

              <div v-if="result.credentials && result.credentials.length > 0" class="mb-5">
                <div class="section-label">CERTIFICATIONS & CREDENTIALS</div>
                <v-divider class="mb-2"></v-divider>
                <div class="text-body-2">{{ result.credentials.join(' | ') }}</div>
              </div>

              <div v-if="result.additionalInfo && result.additionalInfo.length > 0">
                <div class="section-label">ADDITIONAL INFORMATION</div>
                <v-divider class="mb-2"></v-divider>
                <div v-for="(info, ii) in result.additionalInfo" :key="'ai-' + ii" class="text-body-2">{{ info }}</div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined class="pa-4 mb-4">
              <div class="text-subtitle-1 font-weight-medium mb-3">
                <v-icon small class="mr-1" color="amber darken-2">mdi-sync-alert</v-icon>
                Sync Notes
              </div>
              <div v-if="!syncNotes || syncNotes.length === 0" class="text-body-2 text--secondary text-center py-4">
                No gaps detected — strong match!
              </div>
              <div v-for="(note, ni) in syncNotes" :key="'sn-' + ni" class="mb-3">
                <v-card flat class="pa-3" :class="getSyncNoteClass(note.type)">
                  <div class="d-flex align-start">
                    <v-icon small class="mr-2 mt-1" :color="getSyncNoteColor(note.type)">{{ getSyncNoteIcon(note.type) }}</v-icon>
                    <div>
                      <div class="text-caption font-weight-medium text-uppercase mb-1" :class="getSyncNoteColor(note.type) + '--text'">{{ note.type }}</div>
                      <div class="text-body-2">{{ note.message }}</div>
                    </div>
                  </div>
                </v-card>
              </div>
            </v-card>

            <v-card outlined class="pa-4">
              <div class="text-subtitle-1 font-weight-medium mb-3">Actions</div>
              <v-btn color="primary" block class="mb-2" :loading="downloading" @click="downloadDocx">
                <v-icon left small>mdi-file-word-box</v-icon>
                Download DOCX
              </v-btn>
              <v-btn outlined color="primary" block class="mb-2" @click="downloadPdf">
                <v-icon left small>mdi-file-pdf-box</v-icon>
                Download PDF
              </v-btn>
              <v-btn outlined color="success" block class="mb-2" :loading="saving" @click="showSaveDialog">
                <v-icon left small>mdi-content-save</v-icon>
                Save to My Resumes
              </v-btn>
              <v-btn text block class="mb-2" @click="copyToClipboard">
                <v-icon left small>mdi-content-copy</v-icon>
                Copy to Clipboard
              </v-btn>
              <v-btn text block class="mb-2" @click="startOver">
                <v-icon left small>mdi-refresh</v-icon>
                Try Another JD
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div v-if="step === 'error'" class="text-center py-8">
        <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
        <div class="text-h6 mb-2">Something went wrong</div>
        <div class="text-body-2 text--secondary mb-4">{{ errorMessage }}</div>
        <v-btn color="primary" @click="startOver">Try Again</v-btn>
      </div>

      <v-dialog v-model="saveDialog" max-width="450" persistent>
        <v-card>
          <v-card-title>Save to My Resumes</v-card-title>
          <v-card-text>
            <p class="text-body-2 mb-3">Save this targeted resume for easy access later.</p>
            <v-text-field
              v-model="saveResumeName"
              label="Name this resume"
              placeholder="e.g. Director of Strategy - Expedia"
              outlined
              dense
              :rules="[v => !!v || 'Name is required']"
            ></v-text-field>
            <v-textarea
              v-model="saveResumeNote"
              label="Notes (optional)"
              placeholder="e.g. Tailored for the VP Strategy role posted Jan 2026"
              outlined
              dense
              rows="2"
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="px-6 pb-4">
            <v-spacer></v-spacer>
            <v-btn text @click="saveDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="saving" :disabled="!saveResumeName.trim()" @click="saveResume">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
        {{ snackbarText }}
      </v-snackbar>
    </v-container>
  </page-main>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapState } from "vuex";
import { Post } from "../../api/ApiBase";
import { GetProfileContext } from "../../api/ProfileContextApi";
import { CreateReview } from "../../api/CareerApiUser";
import DocxBuilder from "../../docxgen/DocxBuilder";

export default {
  name: "TargetedResumeBuilder",
  data: function() {
    return {
      step: "input",
      jobDescription: "",
      generating: false,
      downloading: false,
      saving: false,
      saveDialog: false,
      saveResumeName: "",
      saveResumeNote: "",
      result: {},
      syncNotes: [],
      additionalContext: "",
      keepAllJobs: false,
      errorMessage: "",
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      profileName: "",
      pageLength: 1,
      pageLengthOptions: [
        { text: "1 Page - curated, most relevant roles only", value: 1 },
        { text: "2 Pages - comprehensive, all roles included", value: 2 }
      ]
    };
  },
  computed: {
    displayName: function() {
      var first = this.$store.state.information?.firstName || "";
      var last = this.$store.state.information?.lastName || "";
      var storeName = (first + " " + last).trim();
      if (storeName) return storeName;
      if (this.profileName) return this.profileName;
      return "Your Name";
    },
    displayLocation: function() {
      var city = this.$store.state.address?.city || "";
      var st = this.$store.state.address?.state || "";
      var country = this.$store.state.address?.country || "";
      var parts = [city, st].filter(function(s) { return s; }).join(", ");
      if (country && country !== "United States" && country !== "US" && country !== "USA") {
        parts = parts ? parts + ", " + country : country;
      }
      return parts;
    },
    displayEmail: function() {
      return this.$store.state.address?.email || "";
    },
    displayPhone: function() {
      return this.$store.state.address?.phoneNumber || "";
    },
    displayContactLine: function() {
      var parts = [];
      if (this.displayLocation) parts.push(this.displayLocation);
      if (this.displayEmail) parts.push(this.displayEmail);
      if (this.displayPhone) parts.push(this.displayPhone);
      return parts.join("  |  ");
    },
    ...mapFields("auth", { isPremium: "isPremium" }),
    ...mapState("settings", ["companyViewEnabled"]),
  },
  mounted: function() {
    // PR-UPGRADE-MODAL: gating unchanged. A free user should not be on this
    // page at all, so open the shared upgrade modal and send them back to a
    // surface that works for them (history or CareerProfile fallback).
    if (!this.isPremium && !this.companyViewEnabled) {
      this.$store.dispatch("upgradeModal/open", {
        featureName: "the Targeted Resume Builder",
        message:
          "The Targeted Resume Builder is part of the AI tools. Plus turns them on, along with unlimited Sage coaching, whenever you want more."
      });
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.replace({ path: "/CareerProfile" });
      }
    }
  },
  methods: {
    jdRule: function(v) {
      if (!v) return true;
      if (v.length > 8000) return "Maximum 8,000 characters";
      return true;
    },
    contextRule: function(v) {
      if (!v) return true;
      if (v.length > 2000) return "Maximum 2,000 characters";
      return true;
    },
    onPageLengthChange: function(value) {
      // Default the keep-all-jobs toggle ON for the 2-page resume (where
      // dropped roles create employment gaps), OFF for the 1-pager (which is
      // intentionally curated). The user can still override either way.
      this.keepAllJobs = value === 2;
    },
    generateResume: async function() {
      var self = this;
      self.generating = true;
      self.step = "loading";

      try {
        var resumeId = self.$store.state.resumeId;
        var contextResult = await GetProfileContext(resumeId, true);
        if (!contextResult.success || !contextResult.data) {
          self.errorMessage = "Could not load your profile data. Please try again.";
          self.step = "error";
          self.generating = false;
          return;
        }

        var ctxName = contextResult.data.name;
        if (ctxName) {
          var fullName = ((ctxName.firstName || "") + " " + (ctxName.lastName || "")).trim();
          if (fullName) self.profileName = fullName;
        }

        var profileData = self.buildProfileText(contextResult.data);

        var response = await Post("/api/Resume/GenerateTargeted", {
          jobDescription: self.jobDescription,
          profileData: profileData,
          resumeId: resumeId,
          pageLength: self.pageLength,
          additionalContext: self.additionalContext
        });

        if (response.success && response.data) {
          var data = response.data;
          if (typeof data === "string") {
            try { data = JSON.parse(data); } catch(e) { /* use as-is */ }
          }
          self.result = data;
          // Deterministic safety net: the model cannot be trusted to keep every
          // role, so when "Keep all jobs" is on we reconcile the AI output
          // against the real profile work history and append any dropped roles
          // using ONLY the candidate's real achievement text.
          if (self.keepAllJobs) {
            self.reconcileExperience(contextResult.data);
          }
          self.syncNotes = data.syncNotes || [];
          self.step = "result";
        } else {
          self.errorMessage = (response.data && response.data.message) || "Failed to generate resume. Please try again.";
          self.step = "error";
        }
      } catch(e) {
        self.errorMessage = "An unexpected error occurred. Please try again.";
        self.step = "error";
      }
      self.generating = false;
    },
    buildProfileText: function(ctx) {
      if (!ctx) return "";
      var parts = [];
      var name = ((ctx.name && ctx.name.firstName) || "") + " " + ((ctx.name && ctx.name.lastName) || "");
      name = name.trim();
      if (name) parts.push("Name: " + name);

      var work = ctx.workExperience || [];
      if (work.length > 0) {
        var workLines = [];
        for (var i = 0; i < work.length; i++) {
          var job = work[i];
          var title = (job.positions && job.positions.length > 0) ? job.positions[0].title : "";
          var company = job.company || "";
          var startDate = job.startDate || "";
          var endDate = job.isCurrent ? "Present" : (job.endDate || "");
          // Mirror the deterministic re-add path (reconcileExperience) which
          // builds location from job.city + job.state. The previous read of
          // job.location was always blank in the profile context payload, so
          // experience[].location came back empty/"N/A" on the rendered resume.
          var location = [job.city, job.state].filter(function(s) { return s; }).join(", ");
          var header = (title || "Role") + " at " + (company || "Company");
          if (startDate || endDate) header += " (" + startDate + " - " + endDate + ")";
          workLines.push(header);
          if (location) workLines.push("  Location: " + location);
          var achs = job.achievements || [];
          for (var a = 0; a < achs.length; a++) {
            var desc = achs[a].description || achs[a].displayCustom || achs[a].displayAuto || "";
            if (desc) workLines.push("  - " + desc);
          }
          var resps = job.responsibilities || [];
          for (var r = 0; r < resps.length; r++) {
            var resp = resps[r].description || resps[r].displayCustom || resps[r].displayAuto || "";
            if (resp) workLines.push("  - " + resp);
          }
        }
        if (workLines.length > 0) parts.push("Work Experience:\n" + workLines.join("\n"));
      }

      var skills = ctx.skills || {};
      var allSkills = [].concat(skills.hard || [], skills.soft || [], skills.technical || []);
      if (allSkills.length > 0) parts.push("Skills: " + allSkills.join(", "));

      var edu = ctx.education || [];
      if (edu.length > 0) {
        var eduLines = [];
        for (var e = 0; e < edu.length; e++) {
          var ed = edu[e];
          var line = (ed.degree || "") + (ed.fieldOfStudy ? " in " + ed.fieldOfStudy : "") + (ed.school ? " from " + ed.school : "");
          if (ed.location) line += ", " + ed.location;
          var eduStart = ed.startDate ? this.formatDateForDisplay(ed.startDate) : "";
          var eduEnd = ed.endDate ? this.formatDateForDisplay(ed.endDate) : (ed.graduationDate ? this.formatDateForDisplay(ed.graduationDate) : "");
          if (eduStart || eduEnd) line += " (" + (eduStart ? eduStart + " - " : "") + (eduEnd || "") + ")";
          if (line.trim()) eduLines.push(line.trim());
        }
        if (eduLines.length > 0) parts.push("Education:\n" + eduLines.join("\n"));
      }

      var wins = ctx.smallWins || [];
      if (wins.length > 0) {
        var winLines = [];
        for (var w = 0; w < wins.length; w++) {
          var win = wins[w];
          var winDesc = win.description || "";
          if (winDesc) {
            var winDetail = "- " + winDesc;
            if (win.context) winDetail += " | Situation: " + win.context;
            if (win.action) winDetail += " | Action: " + win.action;
            if (win.outcome) winDetail += " | Result: " + win.outcome;
            winLines.push(winDetail);
          }
        }
        if (winLines.length > 0) parts.push("Small Wins & Achievements:\n" + winLines.join("\n"));
      }

      if (ctx.professionalSummary) parts.push("Professional Summary: " + ctx.professionalSummary);
      var certs = ctx.certifications || [];
      if (certs.length > 0) parts.push("Certifications: " + certs.join(", "));
      var orgs = ctx.organizations || [];
      if (orgs.length > 0) parts.push("Organizations: " + orgs.join(", "));
      var languages = ctx.languages || [];
      if (languages.length > 0) parts.push("Languages: " + languages.join(", "));

      return parts.join("\n\n");
    },
    reconcileExperience: function(ctx) {
      // Append any profile role the AI dropped, built from REAL profile data
      // only. Match on company + title (case-insensitive, trimmed). Re-added
      // roles get their bullets from the role's actual achievement text; a role
      // with no achievements is still included (company/title/dates) so there
      // is no employment gap. Final order is chronological (most recent first)
      // so re-added roles slot into their natural place.
      var self = this;
      if (!ctx) return;
      var work = ctx.workExperience || [];
      if (work.length === 0) return;

      if (!self.result) self.result = {};
      var experience = Array.isArray(self.result.experience) ? self.result.experience : [];

      var norm = function(s) { return (s || "").toString().trim().toLowerCase(); };
      var keyOf = function(company, title) { return norm(company) + "||" + norm(title); };

      var presentKeys = {};
      for (var i = 0; i < experience.length; i++) {
        presentKeys[keyOf(experience[i].company, experience[i].title)] = true;
      }

      for (var w = 0; w < work.length; w++) {
        var job = work[w];
        var company = job.company || "";
        var title = (job.positions && job.positions.length > 0) ? (job.positions[0].title || "") : "";
        if (!company && !title) continue;

        var k = keyOf(company, title);
        if (presentKeys[k]) continue;
        presentKeys[k] = true;

        var location = [job.city, job.state].filter(function(s) { return s; }).join(", ");
        var startDate = job.startDate ? self.formatDateForDisplay(job.startDate) : "";
        var endDate = job.isCurrent ? "Present" : (job.endDate ? self.formatDateForDisplay(job.endDate) : "");

        var bullets = [];
        var achs = job.achievements || [];
        for (var a = 0; a < achs.length; a++) {
          var desc = achs[a].description || achs[a].displayCustom || achs[a].displayAuto || "";
          if (desc) bullets.push(desc);
        }

        experience.push({
          company: company,
          title: title,
          location: location,
          startDate: startDate,
          endDate: endDate,
          bullets: bullets,
          // sort key from the raw profile date (yyyy-MM-dd) so chronological
          // ordering is stable regardless of the display formatting above.
          _sortDate: job.isCurrent ? "9999-99-99" : (job.endDate || job.startDate || "")
        });
      }

      // Keep the AI's existing entries first (most-relevant ordering) but make
      // sure re-added roles fall into chronological order among themselves.
      var aiEntries = [];
      var addedEntries = [];
      for (var e = 0; e < experience.length; e++) {
        if (Object.prototype.hasOwnProperty.call(experience[e], "_sortDate")) {
          addedEntries.push(experience[e]);
        } else {
          aiEntries.push(experience[e]);
        }
      }
      addedEntries.sort(function(x, y) {
        if (x._sortDate < y._sortDate) return 1;
        if (x._sortDate > y._sortDate) return -1;
        return 0;
      });
      for (var d = 0; d < addedEntries.length; d++) {
        delete addedEntries[d]._sortDate;
      }

      self.result = Object.assign({}, self.result, {
        experience: aiEntries.concat(addedEntries)
      });
    },
    formatDateForDisplay: function(dateStr) {
      if (!dateStr) return "";
      var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      var parts = dateStr.split("-");
      if (parts.length >= 2) {
        var year = parts[0];
        var monthIdx = parseInt(parts[1], 10) - 1;
        if (monthIdx >= 0 && monthIdx < 12) return months[monthIdx] + " " + year;
        return year;
      }
      return dateStr;
    },
    getSyncNoteClass: function(type) {
      if (type === "gap") return "sync-note-gap";
      if (type === "weak") return "sync-note-weak";
      if (type === "strength") return "sync-note-strength";
      if (type === "length") return "sync-note-omitted";
      return "sync-note-omitted";
    },
    getSyncNoteColor: function(type) {
      if (type === "gap") return "error";
      if (type === "weak") return "amber darken-2";
      if (type === "strength") return "success";
      if (type === "length") return "blue-grey";
      return "grey";
    },
    getSyncNoteIcon: function(type) {
      if (type === "gap") return "mdi-alert-circle";
      if (type === "weak") return "mdi-alert";
      if (type === "strength") return "mdi-check-circle";
      if (type === "length") return "mdi-file-document-outline";
      return "mdi-information";
    },
    buildResumeHtml: function() {
      var name = this.displayName;
      var location = this.displayLocation;
      var html = [];
      html.push('<div style="font-family: Calibri, Arial, sans-serif; color: #222; max-width: 700px; margin: 0 auto; padding: 40px 50px; font-size: 11pt; line-height: 1.5;">');
      html.push('<div style="margin-bottom: 20px;">');
      html.push('<div style="font-size: 18pt; font-weight: bold; text-align: left;">' + this.escHtml(name) + '</div>');
      if (location) html.push('<div style="font-size: 10pt; color: #555; text-align: left;">' + this.escHtml(location) + '</div>');
      html.push('</div>');

      if (this.result.professionalSummary) {
        html.push('<div style="margin-bottom: 16px;">');
        html.push('<div style="font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #1E3A5F; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 6px;">Professional Summary</div>');
        html.push('<div style="font-size: 10.5pt;">' + this.escHtml(this.result.professionalSummary) + '</div>');
        html.push('</div>');
      }

      if (this.result.skillsAndAbilities && this.result.skillsAndAbilities.length > 0) {
        html.push('<div style="margin-bottom: 16px;">');
        html.push('<div style="font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #1E3A5F; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 6px;">Skills and Abilities</div>');
        for (var s = 0; s < this.result.skillsAndAbilities.length; s++) {
          var group = this.result.skillsAndAbilities[s];
          html.push('<div style="font-size: 10.5pt; margin-bottom: 4px;"><strong>' + this.escHtml(group.category) + ':</strong> ' + this.escHtml((group.items || []).join(", ")) + '</div>');
        }
        html.push('</div>');
      }

      if (this.result.experience && this.result.experience.length > 0) {
        html.push('<div style="margin-bottom: 16px;">');
        html.push('<div style="font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #1E3A5F; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 6px;">Experience</div>');
        for (var j = 0; j < this.result.experience.length; j++) {
          var job = this.result.experience[j];
          html.push('<div style="margin-bottom: 12px;">');
          html.push('<div style="display: flex; justify-content: space-between;">');
          html.push('<div><strong style="font-size: 10.5pt;">' + this.escHtml(job.title) + '</strong><br/><span style="font-size: 10pt; color: #555;">' + this.escHtml(job.company) + (job.location ? ", " + this.escHtml(job.location) : "") + '</span></div>');
          html.push('<div style="font-size: 10pt; color: #555; white-space: nowrap; text-align: right;">' + this.escHtml(job.startDate || "") + ' – ' + this.escHtml(job.endDate || "") + '</div>');
          html.push('</div>');
          if (job.bullets && job.bullets.length > 0) {
            html.push('<ul style="margin: 6px 0 0 20px; padding: 0;">');
            for (var b = 0; b < job.bullets.length; b++) {
              html.push('<li style="font-size: 10.5pt; margin-bottom: 3px;">' + this.escHtml(job.bullets[b]) + '</li>');
            }
            html.push('</ul>');
          }
          html.push('</div>');
        }
        html.push('</div>');
      }

      if (this.result.education && this.result.education.length > 0) {
        html.push('<div style="margin-bottom: 16px;">');
        html.push('<div style="font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #1E3A5F; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 6px;">Education</div>');
        for (var e = 0; e < this.result.education.length; e++) {
          var edu = this.result.education[e];
          html.push('<div style="display: flex; justify-content: space-between; margin-bottom: 4px;">');
          html.push('<div><strong style="font-size: 10.5pt;">' + this.escHtml(edu.degree) + '</strong><br/><span style="font-size: 10pt; color: #555;">' + this.escHtml(edu.school) + (edu.location ? ", " + this.escHtml(edu.location) : "") + '</span></div>');
          html.push('<div style="font-size: 10pt; color: #555;">' + this.escHtml(edu.dates || "") + '</div>');
          html.push('</div>');
        }
        html.push('</div>');
      }

      if (this.result.credentials && this.result.credentials.length > 0) {
        html.push('<div style="margin-bottom: 16px;">');
        html.push('<div style="font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #1E3A5F; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 6px;">Certifications & Credentials</div>');
        html.push('<div style="font-size: 10.5pt;">' + this.escHtml(this.result.credentials.join(" | ")) + '</div>');
        html.push('</div>');
      }

      if (this.result.additionalInfo && this.result.additionalInfo.length > 0) {
        html.push('<div style="margin-bottom: 16px;">');
        html.push('<div style="font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #1E3A5F; border-bottom: 1px solid #ccc; padding-bottom: 2px; margin-bottom: 6px;">Additional Information</div>');
        for (var ai = 0; ai < this.result.additionalInfo.length; ai++) {
          html.push('<div style="font-size: 10.5pt;">' + this.escHtml(this.result.additionalInfo[ai]) + '</div>');
        }
        html.push('</div>');
      }

      html.push('</div>');
      return html.join("");
    },
    escHtml: function(str) {
      if (!str) return "";
      return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    },
    downloadPdf: function() {
      var resumeHtml = this.buildResumeHtml();
      var printWindow = window.open("", "_blank");
      if (!printWindow) {
        this.snackbarText = "Please allow popups to download PDF";
        this.snackbarColor = "warning";
        this.snackbar = true;
        return;
      }
      printWindow.document.write('<!DOCTYPE html><html><head><title>' + this.escHtml(this.displayName) + ' - Targeted Resume</title>');
      printWindow.document.write('<style>@page { margin: 0.5in 0.6in; size: letter; } body { margin: 0; padding: 0; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(resumeHtml);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      setTimeout(function() {
        printWindow.focus();
        printWindow.print();
      }, 400);
    },
    buildDocxBlob: async function() {
      var doc = new DocxBuilder();
      doc.AddTitle(this.displayName);
      if (this.displayContactLine) doc.AddLeftAlignedText(this.displayContactLine);

      if (this.result.professionalSummary) {
        doc.AddHeading1("PROFESSIONAL SUMMARY", true);
        doc.AddParagraph(this.result.professionalSummary);
      }

      if (this.result.skillsAndAbilities && this.result.skillsAndAbilities.length > 0) {
        var skillLabels = [];
        var skillValues = [];
        for (var s = 0; s < this.result.skillsAndAbilities.length; s++) {
          var group = this.result.skillsAndAbilities[s];
          skillLabels.push((group.category || "Skills") + ": ");
          skillValues.push((group.items || []).join(", "));
        }
        doc.AddHeading1("SKILLS AND ABILITIES", true);
        doc.AddFormattedBulletList(skillLabels, skillValues);
      }

      if (this.result.experience && this.result.experience.length > 0) {
        doc.AddHeading1("EXPERIENCE", true);
        for (var j = 0; j < this.result.experience.length; j++) {
          var job = this.result.experience[j];
          var dateLine = (job.startDate || "") + " – " + (job.endDate || "");
          doc.AddSplitHeading(job.company || "", job.location || "");
          doc.AddSplitSubHeading(job.title || "", dateLine);
          if (job.bullets && job.bullets.length > 0) {
            doc.AddBulletList(job.bullets);
          }
        }
      }

      if (this.result.education && this.result.education.length > 0) {
        doc.AddHeading1("EDUCATION", true);
        for (var e = 0; e < this.result.education.length; e++) {
          var edu = this.result.education[e];
          var eduDates = edu.dates || "";
          if (!eduDates && (edu.startDate || edu.endDate)) {
            eduDates = (edu.startDate || "") + " – " + (edu.endDate || "");
          }
          doc.AddSplitHeading(edu.school || "", edu.location || "");
          doc.AddSplitSubHeading(edu.degree || "", eduDates);
        }
      }

      if (this.result.credentials && this.result.credentials.length > 0) {
        doc.AddHeading1("CERTIFICATIONS & CREDENTIALS", true);
        doc.AddParagraph(this.result.credentials.join(" | "));
      }

      if (this.result.additionalInfo && this.result.additionalInfo.length > 0) {
        doc.AddHeading1("ADDITIONAL INFORMATION", true);
        doc.AddBulletList(this.result.additionalInfo);
      }

      return await doc.BuildSection().GetBlob();
    },
    downloadDocx: async function() {
      var self = this;
      self.downloading = true;
      try {
        var blob = await self.buildDocxBlob();
        var link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = (self.displayName || "Resume").replace(/[^a-zA-Z0-9 ]/g, "") + " - Targeted Resume.docx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(function() { URL.revokeObjectURL(link.href); }, 7000);
        self.snackbarText = "Resume downloaded";
        self.snackbarColor = "success";
        self.snackbar = true;
      } catch(e) {
        self.snackbarText = "Error building DOCX — try PDF instead";
        self.snackbarColor = "error";
        self.snackbar = true;
      }
      self.downloading = false;
    },
    copyToClipboard: function() {
      var self = this;
      var text = self.buildPlainText();
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(function() {
          self.snackbarText = "Resume copied to clipboard";
          self.snackbarColor = "success";
          self.snackbar = true;
        });
      }
    },
    buildPlainText: function() {
      var lines = [];
      lines.push(this.displayName);
      if (this.displayLocation) lines.push(this.displayLocation);
      lines.push("");

      if (this.result.professionalSummary) {
        lines.push("PROFESSIONAL SUMMARY");
        lines.push(this.result.professionalSummary);
        lines.push("");
      }

      if (this.result.skillsAndAbilities) {
        lines.push("SKILLS AND ABILITIES");
        for (var s = 0; s < this.result.skillsAndAbilities.length; s++) {
          var group = this.result.skillsAndAbilities[s];
          lines.push(group.category + ": " + (group.items || []).join(", "));
        }
        lines.push("");
      }

      if (this.result.experience) {
        lines.push("EXPERIENCE");
        for (var j = 0; j < this.result.experience.length; j++) {
          var job = this.result.experience[j];
          lines.push(job.title + " | " + job.company + (job.location ? ", " + job.location : "") + " | " + job.startDate + " - " + job.endDate);
          var bullets = job.bullets || [];
          for (var b = 0; b < bullets.length; b++) {
            lines.push("  - " + bullets[b]);
          }
          lines.push("");
        }
      }

      if (this.result.education) {
        lines.push("EDUCATION");
        for (var e = 0; e < this.result.education.length; e++) {
          var edu = this.result.education[e];
          lines.push(edu.degree + " | " + edu.school + (edu.dates ? " | " + edu.dates : ""));
        }
        lines.push("");
      }

      if (this.result.credentials && this.result.credentials.length > 0) {
        lines.push("CERTIFICATIONS");
        lines.push(this.result.credentials.join(" | "));
      }

      return lines.join("\n");
    },
    showSaveDialog: function() {
      if (!this.saveResumeName) {
        var title = "";
        if (this.result.experience && this.result.experience.length > 0) {
          title = this.result.experience[0].title || "";
        }
        this.saveResumeName = title ? "Targeted - " + title : "Targeted Resume";
      }
      this.saveDialog = true;
    },
    toBase64: function(blob) {
      return new Promise(function(resolve, reject) {
        var reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = function() {
          resolve(reader.result.slice(reader.result.indexOf("base64,", 0) + 7));
        };
        reader.onerror = function(error) { reject(error); };
      });
    },
    saveResume: async function() {
      var self = this;
      self.saving = true;
      try {
        var blob = await self.buildDocxBlob();
        var base64 = await self.toBase64(blob);
        var result = await CreateReview(
          self.saveResumeName,
          self.saveResumeNote,
          base64,
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          (self.displayName || "Resume").replace(/[^a-zA-Z0-9 ]/g, "") + " - Targeted Resume.docx"
        );
        if (result.success) {
          self.saveDialog = false;
          self.snackbarText = "Resume saved to My Resumes";
          self.snackbarColor = "success";
          self.snackbar = true;
          self.$router.push("/SavedResumes");
        } else {
          self.snackbarText = "Error saving resume. Please try again.";
          self.snackbarColor = "error";
          self.snackbar = true;
        }
      } catch(e) {
        self.snackbarText = "Error saving resume. Please try again.";
        self.snackbarColor = "error";
        self.snackbar = true;
      }
      self.saving = false;
    },
    startOver: function() {
      this.step = "input";
      this.jobDescription = "";
      this.result = {};
      this.syncNotes = [];
      this.errorMessage = "";
      this.saveResumeName = "";
      this.saveResumeNote = "";
      this.additionalContext = "";
    }
  }
};
</script>

<style scoped>
.resume-preview-card {
  max-width: 800px;
}

.section-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--v-primary-base, #1E3A5F);
  margin-bottom: 4px;
}

.resume-bullets {
  padding-left: 20px;
  list-style-type: disc;
}

.sync-note-gap {
  background: rgba(244, 67, 54, 0.06);
  border-left: 3px solid #f44336;
  border-radius: 4px;
}

.sync-note-weak {
  background: rgba(255, 160, 0, 0.06);
  border-left: 3px solid #ffa000;
  border-radius: 4px;
}

.sync-note-omitted {
  background: rgba(158, 158, 158, 0.06);
  border-left: 3px solid #9e9e9e;
  border-radius: 4px;
}

.sync-note-strength {
  background: rgba(76, 175, 80, 0.06);
  border-left: 3px solid #4caf50;
  border-radius: 4px;
}
</style>

<style>
.theme--dark .section-label {
  color: #90CAF9;
}

.theme--dark .sync-note-gap {
  background: rgba(244, 67, 54, 0.12);
}

.theme--dark .sync-note-weak {
  background: rgba(255, 160, 0, 0.12);
}

.theme--dark .sync-note-omitted {
  background: rgba(158, 158, 158, 0.12);
}

.theme--dark .sync-note-strength {
  background: rgba(76, 175, 80, 0.12);
}

/* The resume preview is a WYSIWYG of the printed page, so keep it paper-white
   with dark, readable text even in dark mode (the card would otherwise theme
   dark and the resume would read light-on-dark). Scoped to the preview card. */
.theme--dark .resume-preview-card {
  background-color: #ffffff !important;
  color: rgba(0, 0, 0, 0.87) !important;
}
.theme--dark .resume-preview-card .text--secondary {
  color: rgba(0, 0, 0, 0.6) !important;
}
.theme--dark .resume-preview-card .section-label {
  color: #1e3a5f !important;
}
.theme--dark .resume-preview-card .v-divider {
  border-color: rgba(0, 0, 0, 0.12) !important;
}
</style>
